import { ISymbolDetail } from '@aqx-core/common/api/market';
import { EOrderType } from '@aqx-core/common/api/order';

import {
  calculateStopLossTakeProfitPriceLimit,
  checkIsOpenPosition,
  priceToPoints,
  stopLossPointsToPrice,
  takeProfitPointsToPrice
} from '../../../../calculation/order';
import { INormalizeParams, IPricePointsParams, IPricePointsRange } from './types';
import { calculatePointsFromPrice, calculatePriceFromPoints, getLivePriceByOrderType } from './utils';
import { validateStopLoss, validateTakeProfit } from './validation';

export class PricePointsController {
  private emptyPricePoint = {
    price: '',
    points: ''
  };

  protected calculatePointsIncrease = (currentValue: string | undefined, minValue: number): string => {
    const parsed = Number(currentValue);
    const currentPoints = Number.isFinite(parsed) ? parsed : 0;

    // When field is empty/zero or below minimum, start at minimum (or 1 if minimum is 0)
    if (!currentPoints || currentPoints < minValue) {
      return Math.max(minValue, 1).toString();
    }

    return (currentPoints + 1).toString();
  };

  protected calculatePointsDecrease = (currentValue: string | undefined, minValue: number): string => {
    const parsed = Number(currentValue);
    const currentPoints = Number.isFinite(parsed) ? parsed : 0;

    // When field is empty/zero or at/below minimum, return minimum (or 1 if minimum is 0)
    if (!currentPoints || currentPoints <= Math.max(minValue, 1)) {
      return Math.max(minValue, 1).toString();
    }

    return (currentPoints - 1).toString();
  };

  private calculatePriceIncrease = (
    currentValue: string | undefined,
    referencePrice: number,
    pointStep: number,
    decimal: number,
    minPoints: number,
    orderType: EOrderType,
    priceCalculatorFn: (params: {
      input: number;
      points: number;
      orderType: EOrderType;
      pointStep: number;
    }) => number | undefined
  ): string => {
    const currentPrice = parseFloat(currentValue || '0');

    if (!currentPrice) {
      const minPrice = priceCalculatorFn({
        input: referencePrice,
        points: minPoints,
        orderType,
        pointStep
      });
      return minPrice?.toFixed(decimal) || '';
    } else {
      const newPrice = currentPrice + pointStep;
      return newPrice.toFixed(decimal);
    }
  };

  private calculatePriceDecrease = (
    currentValue: string | undefined,
    referencePrice: number,
    pointStep: number,
    decimal: number,
    minPoints: number,
    orderType: EOrderType,
    priceCalculatorFn: (params: {
      input: number;
      points: number;
      orderType: EOrderType;
      pointStep: number;
    }) => number | undefined
  ): string => {
    const currentPrice = parseFloat(currentValue || '0');

    if (!currentPrice) {
      // Calculate minimum price
      const minPrice = priceCalculatorFn({
        input: referencePrice,
        points: minPoints,
        orderType,
        pointStep
      });
      return minPrice?.toFixed(decimal) || '';
    } else {
      // Calculate new price after decrease
      const newPrice = currentPrice - pointStep;
      // Convert new price to points to check against minimum threshold
      const newPricePoints = priceToPoints({
        input: referencePrice,
        price: newPrice,
        pointStep,
        neutral: true
      });
      // Web-trader validation pattern: Only allow decrease if result would be > minPoints
      if (newPricePoints && newPricePoints > minPoints) {
        return newPrice.toFixed(decimal);
      }

      // If decrease would violate minimum threshold, return minimum price
      const minPrice = priceCalculatorFn({
        input: referencePrice,
        points: minPoints,
        orderType,
        pointStep
      });
      return minPrice?.toFixed(decimal) || '';
    }
  };

  // Enhanced stop loss increase logic with comprehensive business rules
  public onStopLossIncrease = (params: IPricePointsParams): string | undefined => {
    const { currentValue, currentPrice, indicate, symbolDetail, orderType } = params;

    if (orderType === null || orderType === undefined || !symbolDetail) {
      return undefined;
    }

    const { pointStep = 0.0001, decimal = 5, stopsLevel = 10 } = symbolDetail;

    if (indicate === 'POINTS') {
      // For market orders (no currentPrice), can't proceed without reference price
      if (checkIsOpenPosition(orderType) && !currentPrice) {
        return undefined;
      }
      return this.calculatePointsIncrease(currentValue, stopsLevel);
    } else {
      // PRICE mode
      const input = checkIsOpenPosition(orderType)
        ? getLivePriceByOrderType(orderType, symbolDetail)
        : parseFloat(currentPrice || '0');

      if (!input) {
        return undefined;
      }

      return this.calculatePriceIncrease(
        currentValue,
        input,
        pointStep,
        decimal,
        stopsLevel,
        orderType,
        stopLossPointsToPrice
      );
    }
  };

  // Enhanced stop loss decrease logic with comprehensive business rules
  public onStopLossDecrease = (params: IPricePointsParams): string | undefined => {
    const { currentValue, currentPrice, indicate, symbolDetail, orderType } = params;

    if (orderType === null || orderType === undefined || !symbolDetail) {
      return undefined;
    }

    const { pointStep = 0.0001, decimal = 5, stopsLevel = 10 } = symbolDetail;

    if (indicate === 'POINTS') {
      // For market orders (no currentPrice), can't proceed without reference price
      if (checkIsOpenPosition(orderType) && !currentPrice) {
        return undefined;
      }
      return this.calculatePointsDecrease(currentValue, stopsLevel);
    } else {
      // PRICE mode
      const input = checkIsOpenPosition(orderType)
        ? getLivePriceByOrderType(orderType, symbolDetail)
        : parseFloat(currentPrice || '0');

      if (!input) {
        return undefined;
      }

      return this.calculatePriceDecrease(
        currentValue,
        input,
        pointStep,
        decimal,
        stopsLevel,
        orderType,
        stopLossPointsToPrice
      );
    }
  };

  // Enhanced take profit increase logic with comprehensive business rules
  public onTakeProfitIncrease = (params: IPricePointsParams): string | undefined => {
    const { currentValue, currentPrice, indicate, symbolDetail, orderType } = params;

    if (orderType === null || orderType === undefined || !symbolDetail) {
      return undefined;
    }

    const { pointStep = 0.0001, decimal = 5, stopsLevel = 10 } = symbolDetail;

    if (indicate === 'POINTS') {
      // For market orders (no currentPrice), can't proceed without reference price
      if (checkIsOpenPosition(orderType) && !currentPrice) {
        return undefined;
      }
      return this.calculatePointsIncrease(currentValue, stopsLevel); // Take profit minimum is stopsLevel
    } else {
      // PRICE mode
      const input = checkIsOpenPosition(orderType)
        ? getLivePriceByOrderType(orderType, symbolDetail)
        : parseFloat(currentPrice || '0');

      if (!input) {
        return undefined;
      }

      return this.calculatePriceIncrease(
        currentValue,
        input,
        pointStep,
        decimal,
        stopsLevel, // Take profit minimum is stopsLevel (same as stop loss)
        orderType,
        takeProfitPointsToPrice
      );
    }
  };

  // Enhanced take profit decrease logic with comprehensive business rules
  public onTakeProfitDecrease = (params: IPricePointsParams): string | undefined => {
    const { currentValue, currentPrice, indicate, symbolDetail, orderType } = params;

    if (orderType === null || orderType === undefined || !symbolDetail) {
      return undefined;
    }

    const { pointStep = 0.0001, decimal = 5, stopsLevel = 10 } = symbolDetail;

    if (indicate === 'POINTS') {
      // For market orders (no currentPrice), can't proceed without reference price
      if (checkIsOpenPosition(orderType) && !currentPrice) {
        return undefined;
      }
      return this.calculatePointsDecrease(currentValue, stopsLevel); // Take profit minimum is stopsLevel
    } else {
      // PRICE mode
      const input = checkIsOpenPosition(orderType)
        ? getLivePriceByOrderType(orderType, symbolDetail)
        : parseFloat(currentPrice || '0');

      if (!input) {
        return undefined;
      }
      return this.calculatePriceDecrease(
        currentValue,
        input,
        pointStep,
        decimal,
        stopsLevel, // Take profit minimum is stopsLevel (same as stop loss)
        orderType,
        takeProfitPointsToPrice
      );
    }
  };

  // Get price range for stop loss/take profit validation
  public getPriceRange = (params: {
    orderType?: EOrderType;
    symbolDetail?: Partial<ISymbolDetail>;
    currentPrice?: string;
  }): IPricePointsRange => {
    const { orderType, symbolDetail, currentPrice } = params;

    if (orderType === null || orderType === undefined || !symbolDetail) {
      return { stopLossRange: [], takeProfitRange: [] };
    }

    const { pointStep = 0.0001, stopsLevel = 10 } = symbolDetail;
    const inputPrice = parseFloat(currentPrice || '0');
    const livePrice = getLivePriceByOrderType(orderType, symbolDetail);

    if (livePrice) {
      const result = calculateStopLossTakeProfitPriceLimit({
        currentPrice: livePrice,
        inputPrice,
        orderType,
        pointStep,
        stopsLevel
      });

      return {
        stopLossRange: result.stopLossRange.filter((val): val is number => val !== undefined),
        takeProfitRange: result.takeProfitRange.filter((val): val is number => val !== undefined)
      };
    }

    return { stopLossRange: [], takeProfitRange: [] };
  };

  // Comprehensive stop loss validation - delegated to validation module
  public validateStopLoss = validateStopLoss;

  // Comprehensive take profit validation - delegated to validation module
  public validateTakeProfit = validateTakeProfit;

  // Calculate points from price with proper business logic - using shared utility
  public calculatePointsFromPrice = calculatePointsFromPrice;

  // Calculate price from points with proper business logic - using shared utility
  public calculatePriceFromPoints = calculatePriceFromPoints;

  // Format price for SLTP display
  public formatSLTPPrice = (price?: string): string | undefined => {
    if (!price) {
      return undefined;
    }

    // Basic formatting - can be enhanced with proper decimal handling
    return parseFloat(price).toString();
  };

  // Get price description for UI
  public getPricePointsDesc = (params: {
    volume?: string;
    price?: string;
    symbolDetail?: Partial<ISymbolDetail>;
  }): string | undefined => {
    const { volume, price, symbolDetail } = params;

    if (!volume || !price || !symbolDetail) {
      return undefined;
    }

    // Simple description - can be enhanced with proper business logic
    return `Volume: ${volume}, Price: ${price}`;
  };

  // Validate and normalize stop loss price input on blur
  // eslint-disable-next-line complexity
  public validateAndNormalizeStopLossPrice = (
    params: INormalizeParams
  ): { price: string; points: string } | undefined => {
    const { price, entryPrice, buySell, orderType, pointStep, symbolDetail, shouldValidate = true } = params;

    if (!price || !entryPrice || !pointStep || !symbolDetail || orderType === null || orderType === undefined) {
      return undefined;
    }

    const { stopsLevel = 10 } = symbolDetail;

    // Calculate points from the entered price
    const calculatedPoints = this.calculatePointsFromPrice({
      price,
      type: 'SL',
      entryPrice,
      buySell,
      orderType,
      pointStep
    });

    const pointsValue = calculatedPoints ? parseInt(calculatedPoints, 10) : 0;

    // Check if points would be below minimum threshold
    if (pointsValue < stopsLevel && shouldValidate) {
      // Fallback to minimum valid values
      const fallbackPrice = this.calculatePriceFromPoints({
        points: stopsLevel.toString(),
        type: 'SL',
        entryPrice,
        buySell,
        orderType,
        pointStep,
        symbolDetail
      });

      return {
        price: fallbackPrice || price,
        points: stopsLevel.toString()
      };
    }

    const priceBN = parseFloat(price);
    if (isNaN(priceBN)) {
      return this.emptyPricePoint;
    }
    // Valid price - calculate corresponding points
    return {
      price: priceBN.toFixed(symbolDetail.decimal ?? 5),
      points: pointsValue.toString()
    };
  };

  // Validate and normalize take profit price input on blur
  // eslint-disable-next-line complexity
  public validateAndNormalizeTakeProfitPrice = (
    params: INormalizeParams
  ): { price: string; points: string } | undefined => {
    const { price, entryPrice, buySell, orderType, pointStep, symbolDetail, shouldValidate = true } = params;

    if (!price || !entryPrice || !pointStep || !symbolDetail || orderType === null || orderType === undefined) {
      return undefined;
    }

    const { stopsLevel = 10 } = symbolDetail;

    // Calculate points from the entered price
    const calculatedPoints = this.calculatePointsFromPrice({
      price,
      type: 'TP',
      entryPrice,
      buySell,
      orderType,
      pointStep
    });

    const pointsValue = calculatedPoints ? parseInt(calculatedPoints, 10) : 0;

    // Check if points would be below minimum threshold (stopsLevel for take profit)
    if (pointsValue < stopsLevel && shouldValidate) {
      // Fallback to minimum valid values
      const fallbackPrice = this.calculatePriceFromPoints({
        points: stopsLevel.toString(),
        type: 'TP',
        entryPrice,
        buySell,
        orderType,
        pointStep,
        symbolDetail
      });

      return {
        price: fallbackPrice || price,
        points: stopsLevel.toString()
      };
    }

    const priceBN = parseFloat(price);
    if (isNaN(priceBN)) {
      return this.emptyPricePoint;
    }
    // Valid price - calculate corresponding points
    return {
      price: priceBN.toFixed(symbolDetail.decimal ?? 5),
      points: pointsValue.toString()
    };
  };

  // Validate and normalize stop loss points input on blur
  public validateAndNormalizeStopLossPoints = (
    params: INormalizeParams
  ): { price: string; points: string } | undefined => {
    const { points, entryPrice, buySell, orderType, pointStep, symbolDetail, shouldValidate = true } = params;

    if (!points || !entryPrice || !pointStep || !symbolDetail || orderType === null || orderType === undefined) {
      return undefined;
    }

    const { stopsLevel = 10 } = symbolDetail;
    const pointsValue = parseInt(points, 10);

    if (isNaN(pointsValue)) {
      return this.emptyPricePoint;
    }

    // Check if points are below minimum threshold
    if (pointsValue < stopsLevel && shouldValidate) {
      // Fallback to minimum valid values
      const fallbackPrice = this.calculatePriceFromPoints({
        points: stopsLevel.toString(),
        type: 'SL',
        entryPrice,
        buySell,
        orderType,
        pointStep,
        symbolDetail
      });

      return {
        price: fallbackPrice || '',
        points: stopsLevel.toString()
      };
    }

    // Valid points - calculate corresponding price
    const calculatedPrice = this.calculatePriceFromPoints({
      points: pointsValue.toString(),
      type: 'SL',
      entryPrice,
      buySell,
      orderType,
      pointStep,
      symbolDetail
    });

    return {
      price: calculatedPrice || '',
      points: pointsValue.toString()
    };
  };

  // Validate and normalize take profit points input on blur
  public validateAndNormalizeTakeProfitPoints = (
    params: INormalizeParams
  ): { price: string; points: string } | undefined => {
    const { points, entryPrice, buySell, orderType, pointStep, symbolDetail, shouldValidate = true } = params;

    if (!points || !entryPrice || !pointStep || !symbolDetail || orderType === null || orderType === undefined) {
      return undefined;
    }

    const { stopsLevel = 10 } = symbolDetail;
    const pointsValue = parseInt(points, 10);

    if (isNaN(pointsValue)) {
      return this.emptyPricePoint;
    }

    // Check if points are below minimum threshold (stopsLevel for take profit)
    if (pointsValue < stopsLevel && shouldValidate) {
      // Fallback to minimum valid values
      const fallbackPrice = this.calculatePriceFromPoints({
        points: stopsLevel.toString(),
        type: 'TP',
        entryPrice,
        buySell,
        orderType,
        pointStep,
        symbolDetail
      });

      return {
        price: fallbackPrice || '',
        points: stopsLevel.toString()
      };
    }

    // Valid points - calculate corresponding price
    const calculatedPrice = this.calculatePriceFromPoints({
      points: pointsValue.toString(),
      type: 'TP',
      entryPrice,
      buySell,
      orderType,
      pointStep,
      symbolDetail
    });

    return {
      price: calculatedPrice || '',
      points: pointsValue.toString()
    };
  };
}

export const pricePointsController = new PricePointsController();
